import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { getUploadUrl } from "src/Common/common";
import { can } from "src/Common/function";
import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import AttachmentImageSlider from "src/Pages/WO/Components/attachment/AttachmentImageSlider";
import ScannedDocumentsPanel from "../documents/ScannedDocumentsPanel";
import { getDocumentDisplayName } from "../documents/DocumentScanModals";

// Upload flow discriminator understood by the /attach and scan endpoints.
// 3 = proposal, 4 = invoice (independent wo_invoice table).
const INVOICE_UPLOAD_FLOW = 4;

const FILE_ACCEPT = "image/*,application/pdf";
const FILE_FOOTER = "Supported formats: JPG, PNG, HEIC, PDF";

const isPdfFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".pdf");

const getUploaderName = (invoice) =>
  invoice?.uploadedBy?.name ||
  invoice?.uploadedByCustomerUser?.customer?.companyName ||
  invoice?.uploadedByCustomerUser?.account?.name ||
  null;

function WoInvoices({ order, onRefresh }) {
  const currentUser = UserService.getCurrentUser();
  const [sliderIndex, setSliderIndex] = useState(null);

  // Flatten the uploader onto each row so the shared panel can render the
  // "845 KB • Elif Demir • Apr 15, 2026" subline without knowing WO shapes.
  const invoices = useMemo(
    () =>
      (order?.invoiceFiles || []).map((invoice) => ({
        ...invoice,
        uploaderName: getUploaderName(invoice),
      })),
    [order?.invoiceFiles],
  );

  // Map invoices to the shape AttachmentImageSlider expects.
  const sliderAttachments = useMemo(
    () =>
      invoices.map((invoice) => ({
        id: invoice.id,
        fileName: invoice.fileName,
        isPdf: isPdfFile(invoice.fileName),
        displayName: getDocumentDisplayName(invoice),
        description: invoice.description || "",
        tags: invoice.tags || [],
        createdBy: invoice.uploaderName || "-",
        createdAt: invoice.createdAt,
      })),
    [invoices],
  );

  const handleView = (invoice) => {
    if (!invoice?.fileName) return;
    const index = sliderAttachments.findIndex((attachment) => attachment.id === invoice.id);
    if (index >= 0) setSliderIndex(index);
  };

  const uploadFn = async (files, { documentName }) => {
    const previousIds = new Set(invoices.map((invoice) => invoice.id));
    const metadata = documentName ? [{ documentName }] : null;
    const res = await WOService.uploadFiles(files, order.id, INVOICE_UPLOAD_FLOW, currentUser, metadata);
    if (res.status !== 201) {
      toast("Something went wrong");
      return null;
    }
    // The endpoint returns the WO's full invoice list — keep only the new rows.
    return (res.data || []).filter((invoice) => !previousIds.has(invoice.id));
  };

  return (
    <>
      <ScannedDocumentsPanel
        title="Invoices"
        noun="Invoice"
        totalLabel="Work Order's Total Invoice Amount"
        documents={invoices}
        canManage={can("wo:addInvoice")}
        canDelete={can("wo:deleteInvoice")}
        accept={FILE_ACCEPT}
        footer={FILE_FOOTER}
        getFileUrl={(invoice) => getUploadUrl(invoice.fileName)}
        uploadFn={uploadFn}
        scanFn={(id) => WOService.scanDocument(id, INVOICE_UPLOAD_FLOW)}
        confirmFn={(id, amount, sourcePage, sourceLabel) =>
          WOService.confirmDocumentAmount(id, INVOICE_UPLOAD_FLOW, amount, sourcePage, sourceLabel)
        }
        deleteFn={(id) => WOService.deleteInvoice(order.id, id, INVOICE_UPLOAD_FLOW)}
        onRefresh={onRefresh}
        onView={handleView}
      />

      <AttachmentImageSlider
        shown={sliderIndex !== null}
        attachments={sliderAttachments}
        activeIndex={sliderIndex ?? 0}
        onCancel={() => setSliderIndex(null)}
      />
    </>
  );
}

export default WoInvoices;
